// ---------------------------------------------------------------------------
// SurveyPlayer — Interactive survey walkthrough simulator
// ---------------------------------------------------------------------------
// Lets template authors preview the patient experience: one question at a
// time, answer selection drives branching (next_prompt_id), implicit linear
// flow for non-branching prompts. Shows a summary at the end with the full
// path taken and any alert levels triggered.

import { useState, useMemo, useEffect } from 'react';
import {
  Play,
  RotateCcw,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  AlertTriangle,
  Info,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type { TemplateSession, TemplatePrompt, AlertLevel } from '@/types/template';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface StepRecord {
  prompt: TemplatePrompt;
  selectedAnswerIndex: number | null; // null for informational prompts
  alertLevel: AlertLevel | null;
}

interface SurveyPlayerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sessions: TemplateSession[];
  templateTitle: string;
}

// ---------------------------------------------------------------------------
// Alert level display helpers
// ---------------------------------------------------------------------------

const ALERT_LABELS: Record<AlertLevel, string> = {
  0: 'Normal',
  1: 'Notification',
  2: 'Warning',
  3: 'Severe',
  4: 'Critical',
};

const ALERT_COLORS: Record<AlertLevel, string> = {
  0: 'bg-green-100 text-green-700 border-green-200',
  1: 'bg-green-50 text-green-600 border-green-100',
  2: 'bg-honey-100 text-honey-800 border-honey-200',
  3: 'bg-red-100 text-red-700 border-red-200',
  4: 'bg-red-200 text-red-800 border-red-300',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SurveyPlayer({
  open,
  onOpenChange,
  sessions,
  templateTitle,
}: SurveyPlayerProps) {
  // Flatten all prompts and build lookup maps
  const allPrompts = useMemo(
    () => sessions.flatMap((s) => s.prompts),
    [sessions],
  );

  const promptById = useMemo(() => {
    const map = new Map<string, TemplatePrompt>();
    for (const p of allPrompts) map.set(p.prompt_id, p);
    return map;
  }, [allPrompts]);

  const branchTargetIds = useMemo(() => {
    const ids = new Set<string>();
    for (const p of allPrompts) {
      for (const a of p.answers) {
        if (a.next_prompt_id) ids.add(a.next_prompt_id);
      }
    }
    return ids;
  }, [allPrompts]);

  // Build ordered prompt lists per session for implicit linear flow.
  // Branch targets are excluded — they are only reached via explicit routing.
  const sessionOrder = useMemo(() => {
    const map = new Map<number, TemplatePrompt[]>();
    for (const s of sessions) {
      const sorted = s.prompts
        .filter((p) => !branchTargetIds.has(p.prompt_id))
        .sort((a, b) => a.sort_order - b.sort_order);
      map.set(s.sessionNumber, sorted);
    }
    return map;
  }, [sessions, branchTargetIds]);

  // Find the starting prompt: first prompt by sort order in the first session.
  // Uses sort_order rather than is_root so the preview matches the visual
  // prompt tree immediately after drag-and-drop reorder (is_root updates
  // asynchronously via cache refetch).
  const startPrompt = useMemo(() => {
    const sortedSessions = [...sessions].sort(
      (a, b) => a.sessionNumber - b.sessionNumber,
    );
    if (sortedSessions.length > 0 && sortedSessions[0].prompts.length > 0) {
      return [...sortedSessions[0].prompts].sort(
        (a, b) => a.sort_order - b.sort_order,
      )[0];
    }
    return null;
  }, [sessions]);

  // State
  const [currentPrompt, setCurrentPrompt] = useState<TemplatePrompt | null>(
    startPrompt,
  );
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [history, setHistory] = useState<StepRecord[]>([]);
  const [finished, setFinished] = useState(false);

  // Reset state whenever the dialog opens. Covers both external open (parent
  // sets open={true} — Radix does NOT call onOpenChange for this) and
  // startPrompt changing between opens (e.g. after drag-and-drop reorder).
  useEffect(() => {
    if (open) {
      setCurrentPrompt(startPrompt);
      setSelectedAnswer(null);
      setHistory([]);
      setFinished(false);
    }
  }, [open, startPrompt]);

  const handleOpenChange = onOpenChange;

  // Reverse lookup: for each branch-target prompt, find the root prompt
  // whose answer routes to it so we can resume linear flow after a follow-up.
  const parentRootOf = useMemo(() => {
    const map = new Map<string, TemplatePrompt>();
    for (const p of allPrompts) {
      if (branchTargetIds.has(p.prompt_id)) continue;
      for (const a of p.answers) {
        if (a.next_prompt_id) map.set(a.next_prompt_id, p);
      }
    }
    return map;
  }, [allPrompts, branchTargetIds]);

  // Find the next prompt given current state
  function getNextPrompt(
    prompt: TemplatePrompt,
    answerIdx: number | null,
  ): TemplatePrompt | null {
    // If an answer was selected and it has explicit routing, follow it
    if (answerIdx !== null && prompt.answers[answerIdx]?.next_prompt_id) {
      return promptById.get(prompt.answers[answerIdx].next_prompt_id!) ?? null;
    }

    // If current prompt is a follow-up, resume from its parent root prompt
    const rootPrompt = branchTargetIds.has(prompt.prompt_id)
      ? parentRootOf.get(prompt.prompt_id) ?? prompt
      : prompt;

    // Implicit linear flow: next root prompt by sort_order in the same session
    const sessionPrompts = sessionOrder.get(rootPrompt.session_number);
    if (sessionPrompts) {
      const currentIdx = sessionPrompts.findIndex(
        (p) => p.prompt_id === rootPrompt.prompt_id,
      );
      if (currentIdx >= 0 && currentIdx < sessionPrompts.length - 1) {
        return sessionPrompts[currentIdx + 1];
      }
    }

    // End of session — try next session
    const sortedSessionNumbers = [...sessionOrder.keys()].sort(
      (a, b) => a - b,
    );
    const sessionIdx = sortedSessionNumbers.indexOf(rootPrompt.session_number);
    if (sessionIdx >= 0 && sessionIdx < sortedSessionNumbers.length - 1) {
      const nextSessionNum = sortedSessionNumbers[sessionIdx + 1];
      const nextSessionPrompts = sessionOrder.get(nextSessionNum);
      if (nextSessionPrompts && nextSessionPrompts.length > 0) {
        return nextSessionPrompts[0];
      }
    }

    return null; // Survey complete
  }

  function handleNext() {
    if (!currentPrompt) return;

    const alertLevel =
      selectedAnswer !== null
        ? (currentPrompt.answers[selectedAnswer]?.alert_level ?? null)
        : null;

    const step: StepRecord = {
      prompt: currentPrompt,
      selectedAnswerIndex: selectedAnswer,
      alertLevel,
    };

    const next = getNextPrompt(currentPrompt, selectedAnswer);

    setHistory((prev) => [...prev, step]);
    setSelectedAnswer(null);

    if (next) {
      setCurrentPrompt(next);
    } else {
      setFinished(true);
    }
  }

  function handleBack() {
    if (history.length === 0) return;
    const prev = history[history.length - 1];
    setHistory((h) => h.slice(0, -1));
    setCurrentPrompt(prev.prompt);
    setSelectedAnswer(prev.selectedAnswerIndex);
    setFinished(false);
  }

  function handleRestart() {
    setCurrentPrompt(startPrompt);
    setSelectedAnswer(null);
    setHistory([]);
    setFinished(false);
  }

  // Progress info
  const stepNumber = history.length + 1;
  const maxAlertTriggered = useMemo(() => {
    const levels = history
      .map((s) => s.alertLevel)
      .filter((l): l is AlertLevel => l !== null);
    return levels.length > 0 ? Math.max(...levels) : null;
  }, [history]);

  if (!startPrompt) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Survey Preview</DialogTitle>
            <DialogDescription>
              No prompts to preview. Add prompts to the template first.
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Play className="h-4 w-4 text-purple-500" />
            Survey Preview
          </DialogTitle>
          <DialogDescription>
            {templateTitle} — step through as a patient would
          </DialogDescription>
        </DialogHeader>

        {!finished && currentPrompt ? (
          <div className="space-y-4 mt-2">
            {/* Progress bar */}
            <div className="flex items-center justify-between text-xs text-carbon-500">
              <span>
                Step {stepNumber} &middot; Session{' '}
                {currentPrompt.session_number}
              </span>
              <span>#{currentPrompt.sort_order}</span>
            </div>

            {/* Question / Informational card */}
            <div className="rounded-ds-008 border border-silver-200 bg-white p-5">
              <div className="flex items-center gap-2 mb-3">
                {currentPrompt.is_informational ? (
                  <Badge variant="secondary" className="gap-1">
                    <Info className="h-3 w-3" /> Informational
                  </Badge>
                ) : (
                  <Badge variant="default">Question</Badge>
                )}
              </div>

              <p className="text-base text-carbon-900 leading-relaxed">
                {currentPrompt.is_informational
                  ? currentPrompt.prompt_text
                  : currentPrompt.prompt_text ??
                    `(Question #${currentPrompt.question_id})`}
              </p>
            </div>

            {/* Answer options */}
            {!currentPrompt.is_informational &&
              currentPrompt.answers.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-carbon-500 uppercase tracking-wider">
                    Select an answer
                  </p>
                  {currentPrompt.answers
                    .map((answer, originalIdx) => ({ answer, originalIdx }))
                    .sort((a, b) => a.answer.sort_order - b.answer.sort_order)
                    .map(({ answer, originalIdx }) => {
                      const isSelected = selectedAnswer === originalIdx;
                      return (
                        <button
                          key={answer.prompt_answer_id}
                          type="button"
                          className={`w-full text-left rounded-ds-008 border-2 px-4 py-3 text-sm transition-colors ${
                            isSelected
                              ? 'border-purple-500 bg-purple-50'
                              : 'border-silver-200 bg-white hover:border-purple-300 hover:bg-purple-50/30'
                          }`}
                          onClick={() => setSelectedAnswer(originalIdx)}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-carbon-800">
                              {answer.answer_text}
                            </span>
                            {answer.keypress && (
                              <span className="text-xs text-carbon-400 ml-2">
                                Key: {answer.keypress}
                              </span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                </div>
              )}

            {/* Navigation */}
            <div className="flex items-center justify-between pt-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBack}
                disabled={history.length === 0}
              >
                <ArrowLeft className="h-4 w-4 mr-1" /> Back
              </Button>
              <Button
                size="sm"
                onClick={handleNext}
                disabled={
                  !currentPrompt.is_informational &&
                  currentPrompt.answers.length > 0 &&
                  selectedAnswer === null
                }
              >
                {getNextPrompt(currentPrompt, selectedAnswer)
                  ? 'Next'
                  : 'Finish'}
                <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        ) : (
          /* ---- Summary screen ---- */
          <div className="space-y-4 mt-2">
            <div className="rounded-ds-008 border border-green-200 bg-green-50 p-5 text-center">
              <CheckCircle2 className="mx-auto h-8 w-8 text-green-600 mb-2" />
              <p className="text-base font-semibold text-carbon-900">
                Survey Complete
              </p>
              <p className="text-sm text-carbon-500 mt-1">
                {history.length} step{history.length !== 1 ? 's' : ''} taken
              </p>
            </div>

            {/* Max alert triggered */}
            {maxAlertTriggered !== null && maxAlertTriggered > 0 && (
              <div
                className={`flex items-center gap-2 rounded-ds-008 border px-4 py-3 ${ALERT_COLORS[maxAlertTriggered as AlertLevel]}`}
              >
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <span className="text-sm font-medium">
                  Highest alert triggered: L{maxAlertTriggered} (
                  {ALERT_LABELS[maxAlertTriggered as AlertLevel]})
                </span>
              </div>
            )}

            {/* Path summary */}
            <div>
              <p className="text-xs font-medium text-carbon-500 uppercase tracking-wider mb-2">
                Path Taken
              </p>
              <div className="space-y-1">
                {history.map((step, idx) => (
                  <div
                    key={idx}
                    className="flex items-start gap-3 rounded-ds-004 border border-silver-100 bg-white px-3 py-2 text-xs"
                  >
                    <span className="shrink-0 font-mono text-carbon-400 w-5 text-right">
                      {idx + 1}.
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-carbon-800 truncate">
                        {step.prompt.is_informational
                          ? step.prompt.prompt_text
                          : step.prompt.prompt_text ??
                            `Question #${step.prompt.question_id}`}
                      </p>
                      {step.selectedAnswerIndex !== null && (
                        <p className="text-carbon-500 mt-0.5">
                          &rarr;{' '}
                          {
                            step.prompt.answers[step.selectedAnswerIndex]
                              ?.answer_text
                          }
                          {step.alertLevel !== null && step.alertLevel > 0 && (
                            <Badge
                              variant="outline"
                              className={`ml-2 text-[10px] ${ALERT_COLORS[step.alertLevel]}`}
                            >
                              L{step.alertLevel}
                            </Badge>
                          )}
                        </p>
                      )}
                    </div>
                    <span className="shrink-0 text-carbon-400">
                      S{step.prompt.session_number}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between pt-2">
              <Button variant="ghost" size="sm" onClick={handleRestart}>
                <RotateCcw className="h-4 w-4 mr-1" /> Restart
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => handleOpenChange(false)}
              >
                Close
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
